import { useId, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, ShieldAlert } from 'lucide-react';
import { roleNeedsWarehouse, type Scope } from '@eombor/contracts';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Input,
  Skeleton,
  cn,
  useToast,
} from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { rbacApi, type AssignmentDto } from '../api/rbac.api.js';
import {
  ScopePicker,
  scopeForRole,
  scopeTypeLabel,
  scopeTypeOf,
  type ScopeOption,
} from './ScopePicker.js';

/** ISO sanadan faqat kunni oladi — soat biriktiruvda ma'no bermaydi. */
const day = (iso: string): string => iso.slice(0, 10);

const scopeTone = (scope: Scope): 'warn' | 'info' | 'neutral' => {
  if (scope.type === 'GLOBAL') return 'warn';
  return scope.type === 'MTU' ? 'info' : 'neutral';
};

export interface UserAssignmentsProps {
  userId: string;
  /**
   * Komponent OYNA ichida chizilyaptimi.
   *
   * Shunday bo'lsa, rol qo'shish va olib tashlash formalari o'z oynasini
   * ochmaydi — ular joyida, ichkarida chiziladi. Oyna ichidagi oyna `Esc`
   * bosilganda ikkalasini birdan yopadi va foydalanuvchi qayerdan chiqib
   * ketganini tushunmay qoladi.
   */
  nested?: boolean;
  /**
   * Doira uchun tanlanadigan obyektlar (MTU, korxona, ombor). Ro'yxat tashqaridan
   * keladi: bu komponent qaysi kontekstda ochilganini bilmaydi.
   */
  scopeOptions?: ScopeOption[];
}

/**
 * Oyna ichida chiziladigan forma uchun o'ram.
 *
 * `Dialog` bilan bir xil tuzilma (sarlavha, tana, pastki qator) beradi, lekin
 * portal ochmaydi va `Esc` ni ushlamaydi — shuning uchun uni oyna ichida
 * xavfsiz ishlatish mumkin.
 */
const InlinePanel = ({
  title,
  footer,
  children,
}: {
  title: string;
  footer: ReactNode;
  children: ReactNode;
}) => (
  <section className="flex flex-col gap-3 border-t border-border bg-surface-2/50 px-4 py-4">
    <h3 className="text-sm font-semibold text-text">{title}</h3>
    {children}
    <div className="flex flex-wrap items-center justify-end gap-2">{footer}</div>
  </section>
);

const EMPTY_SCOPE: Scope = { type: 'GLOBAL', id: null };

/**
 * Xodimning rol biriktiruvlari.
 *
 * Har bir qatorda doira ko'rinib turadi — chunki aynan shu narsa KMO da yo'q
 * edi. Olib tashlashdan oldin foydalanuvchi nima yo'qotayotganini so'zma-so'z
 * o'qiydi: rol faqat SHU doirada yo'qoladi.
 */
export const UserAssignments = ({
  userId,
  scopeOptions = [],
  nested = false,
}: UserAssignmentsProps) => {
  const { canAnywhere } = usePermission();
  const canManage = canAnywhere('rbac.assignment.manage');
  const toast = useToast();
  const queryClient = useQueryClient();
  const roleFieldId = useId();

  const [addOpen, setAddOpen] = useState(false);
  const [roleId, setRoleId] = useState('');
  const [scope, setScope] = useState<Scope>(EMPTY_SCOPE);
  const [reason, setReason] = useState('');
  const [removing, setRemoving] = useState<AssignmentDto | null>(null);
  /* Tahrir — o'chirib qayta qo'shishning O'RNIGA. Xato doira bilan berilgan
     rolni tuzatish eng ko'p uchraydigan ish va uni ikki qadamga bo'lish
     har safar "qaysi rol edi?" degan savolni tug'dirardi. */
  const [editing, setEditing] = useState<AssignmentDto | null>(null);
  const [editScope, setEditScope] = useState<Scope>(EMPTY_SCOPE);

  const assignmentsKey = ['rbac', 'assignments', userId] as const;

  const assignments = useQuery({
    queryKey: assignmentsKey,
    queryFn: () => rbacApi.listAssignments(userId),
  });

  const roles = useQuery({
    queryKey: ['rbac', 'roles'],
    queryFn: () => rbacApi.listRoles(),
    enabled: canManage,
  });

  const roleItems = roles.data?.items ?? [];
  const permissionsOf = (id: string): readonly string[] =>
    roleItems.find((role) => role.id === id)?.permissions ?? [];

  /**
   * Ombor roli — tanlagich "Ombor" ga qulflanadi.
   *
   * `roles` hali yuklanmagan bo'lsa ro'yxat bo'sh bo'ladi va qulf ishlamaydi:
   * bu ataylab, chunki chegarani BACKEND qo'yadi (`assertScopeForRole`) va
   * yarim yuklangan ekran uni faqat noto'g'ri ko'rsatishi mumkin edi.
   */
  const requiredKindOf = (id: string) =>
    roleNeedsWarehouse(permissionsOf(id)) ? { requiredKind: 'WAREHOUSE' as const } : {};

  const pickRole = (nextId: string): void => {
    setRoleId(nextId);
    setScope((current) => scopeForRole(permissionsOf(nextId), current));
  };

  const openEdit = (assignment: AssignmentDto): void => {
    setEditing(assignment);
    setEditScope(assignment.scope);
  };

  const closeAdd = (): void => {
    setAddOpen(false);
    setRoleId('');
    setScope(EMPTY_SCOPE);
    setReason('');
  };

  const create = useMutation({
    mutationFn: () =>
      rbacApi.createAssignment(userId, {
        roleId,
        scope,
        ...(reason.trim() === '' ? {} : { reason: reason.trim() }),
      }),
    onSuccess: async () => {
      closeAdd();
      await queryClient.invalidateQueries({ queryKey: assignmentsKey });
      toast.show(t('rbac.assignmentCreated'), 'success');
    },
    onError: (error: unknown) => {
      toast.show(
        error instanceof ApiClientError ? error.message : t('rbac.assignmentsLoadError'),
        'danger',
      );
    },
  });

  const update = useMutation({
    mutationFn: (assignment: AssignmentDto) =>
      rbacApi.updateAssignment(assignment.id, { scope: editScope }),
    onSuccess: async () => {
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: assignmentsKey });
      toast.show(t('rbac.assignmentUpdated'), 'success');
    },
    onError: (error: unknown) => {
      toast.show(
        error instanceof ApiClientError ? error.message : t('rbac.assignmentsLoadError'),
        'danger',
      );
    },
  });

  const remove = useMutation({
    mutationFn: (assignment: AssignmentDto) => rbacApi.removeAssignment(assignment.id),
    onSuccess: async () => {
      setRemoving(null);
      await queryClient.invalidateQueries({ queryKey: assignmentsKey });
      toast.show(t('rbac.assignmentRemoved'), 'success');
    },
    onError: (error: unknown) => {
      toast.show(
        error instanceof ApiClientError ? error.message : t('rbac.assignmentsLoadError'),
        'danger',
      );
    },
  });

  const scopeReady = scope.type === 'GLOBAL' || scope.id !== null;

  /**
   * NIMA yetishmayotgani — matn bilan.
   *
   * Ilgari "Saqlash" shunchaki o'chirilgan holda turardi va nega ishlamayotgani
   * hech qayerda yozilmasdi: doira turi tanlangan, lekin QAYSI MTU ekani
   * tanlanmagan holat tashqaridan hech qanday belgi bermasdi. Sababsiz
   * o'chirilgan tugma — odam uchun buzilgan tugma bilan bir xil.
   */
  const missing =
    roleId === '' ? t('rbac.missingRole') : scopeReady ? null : t('rbac.missingScope');
  const scopeLabel = (item: Scope): string => {
    if (item.type === 'GLOBAL') return scopeTypeLabel(item.type);
    const option = scopeOptions.find(
      (o) => scopeTypeOf(o.kind) === item.type && o.id === item.id,
    );
    return `${scopeTypeLabel(item.type)}: ${option?.label ?? `#${item.id ?? '—'}`}`;
  };

  /**
   * Biriktiruvning QAYERI — odam o'qiydigan gap bilan.
   *
   * Ilgari qatorning boshida rol nomi va kodi turardi, doira esa yonidagi
   * kichkina yorliqda edi. Natijada bir xil rol ikki marta ko'rinardi va
   * ular nimasi bilan farq qilishini ekran hech qayerda aytmasdi.
   *
   * Endi birinchi o'ringa QAYERI chiqadi: administratorning savoli
   * «bu odamda qanday rol bor» emas, «bu odam qayerni ko'radi».
   */
  const scopeWhere = (item: Scope): string => {
    if (item.type === 'GLOBAL') return t('rbac.whereGlobal');
    const option = scopeOptions.find(
      (o) => scopeTypeOf(o.kind) === item.type && o.id === item.id,
    );
    const name = option?.label ?? `#${item.id ?? '—'}`;
    return item.type === 'MTU' ? t('rbac.whereMtu', { name }) : name;
  };

  const addBody = (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor={roleFieldId} className="text-sm font-medium text-text">
              {t('rbac.assignmentRole')}
            </label>
            <select
              id={roleFieldId}
              value={roleId}
              onChange={(event) => pickRole(event.target.value)}
              className={cn(
                'h-[var(--size-control)] w-full rounded-[var(--radius-sm)]',
                'border border-border-control bg-surface px-3 text-base text-text',
              )}
            >
              <option value="">{t('rbac.scopeObjectPlaceholder')}</option>
              {(roles.data?.items ?? []).map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>

          <ScopePicker
            value={scope}
            onChange={setScope}
            options={scopeOptions}
            {...requiredKindOf(roleId)}
          />

          <Input
            label={t('rbac.assignmentReason')}
            hint={t('rbac.assignmentReasonHint')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
  );

  /* Tahrirda ROL o'zgarmaydi — u sarlavhada, o'zgarmas ma'lumot bo'lib
     turadi. Boshqa rol kerak bo'lsa, bu boshqa biriktiruv. */
  const editBody =
    editing === null ? null : (
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-text text-sm font-medium">{editing.roleName}</span>
          <span className="tabular text-text-faint text-xs">{editing.roleCode}</span>
        </div>

        <ScopePicker
          value={editScope}
          onChange={setEditScope}
          options={scopeOptions}
          {...requiredKindOf(editing.roleId)}
        />
      </div>
    );

  const editReady = editScope.type === 'GLOBAL' || editScope.id !== null;

  const editFooter = (
    <>
      {editReady ? null : (
        <span className="text-text-muted mr-auto text-sm">{t('rbac.missingScope')}</span>
      )}
      <Button variant="ghost" onClick={() => setEditing(null)}>
        {t('rbac.cancel')}
      </Button>
      <Button
        loading={update.isPending}
        disabled={!editReady}
        onClick={() => {
          if (editing !== null) update.mutate(editing);
        }}
      >
        {t('rbac.save')}
      </Button>
    </>
  );

  return (
    <Card
      title={t('rbac.assignmentsTitle')}
      padded={false}
      actions={
        canManage ? (
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus size={15} aria-hidden />
            {t('rbac.assignmentAdd')}
          </Button>
        ) : null
      }
    >
      <p className="border-b border-border px-4 py-3 text-sm text-text-muted">
        {t('rbac.assignmentsHint')}
      </p>

      {assignments.isPending ? (
        <div className="p-4">
          <Skeleton className="h-24 w-full" />
        </div>
      ) : assignments.isError ? (
        <EmptyState
          title={t('rbac.assignmentsLoadError')}
          description={
            assignments.error instanceof ApiClientError ? assignments.error.message : undefined
          }
          action={
            <Button variant="secondary" onClick={() => void assignments.refetch()}>
              {t('rbac.retry')}
            </Button>
          }
        />
      ) : assignments.data.length === 0 ? (
        <EmptyState
          icon={<ShieldAlert size={20} aria-hidden />}
          title={t('rbac.assignmentsEmpty')}
          description={t('rbac.assignmentsEmptyHint')}
        />
      ) : (
        <ul className="flex flex-col">
          {assignments.data.map((assignment) => (
            <li
              key={assignment.id}
              className={cn(
                'flex flex-wrap items-center justify-between gap-3 border-b border-border',
                'px-4 py-3 last:border-b-0',
                // Butun tizimga berilgan rol qatordan ajralib turadi.
                assignment.scope.type === 'GLOBAL'
                  ? 'shadow-[var(--shadow-rail-warn)]'
                  : undefined,
              )}
            >
              {/*
                QAYERI — birinchi, ROLI — ikkinchi. Ikkalasi almashtirilsa,
                bir xil rol ikki marta ko'rinib, farqi yo'qoladi.

                Rol KODI qatordan olib tashlandi: u administratorga hech
                narsa aytmaydi va aynan u qatorni o'qib bo'lmaydigan qilib
                qo'yardi. Kod `title` da qoladi — kerak bo'lganda ko'rinadi.
              */}
              <div className="flex min-w-0 flex-col gap-1">
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    aria-hidden
                    className={cn(
                      'size-1.5 shrink-0 rounded-full',
                      assignment.scope.type === 'GLOBAL' ? 'bg-warn' : 'bg-accent',
                    )}
                  />
                  <span className="truncate text-sm font-medium text-text">
                    {scopeWhere(assignment.scope)}
                  </span>
                </div>
                <p className="text-xs text-text-muted" title={assignment.roleCode}>
                  {t('rbac.assignmentAs', { role: assignment.roleName })}
                  <span className="tabular text-text-faint">
                    {' · '}
                    {assignment.validTo === null
                      ? t('rbac.assignmentSince', { date: day(assignment.validFrom) })
                      : `${day(assignment.validFrom)} — ${day(assignment.validTo)}`}
                  </span>
                </p>
              </div>

              {canManage ? (
                <div className="flex items-center gap-1">
                  {/* Tahrir — doirani to'g'rilash uchun. Eng ko'p kerak
                      bo'ladigan amal ekanligi uchun u O'CHIRISHDAN oldin
                      turadi: xato doirani tuzatish rolni olib tashlashdan
                      butunlay boshqa narsa. */}
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t('rbac.assignmentEditFor', { role: assignment.roleName })}
                    onClick={() => openEdit(assignment)}
                  >
                    <Pencil size={15} aria-hidden />
                    {t('rbac.assignmentEdit')}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setRemoving(assignment)}>
                    {t('rbac.assignmentRemove')}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {editing !== null && nested ? (
        <InlinePanel title={t('rbac.assignmentEditTitle')} footer={editFooter}>
          {editBody}
        </InlinePanel>
      ) : null}

      {removing !== null && nested ? (
        <InlinePanel
          title={t('rbac.assignmentRemoveTitle')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setRemoving(null)}>
                {t('rbac.cancel')}
              </Button>
              <Button
                variant="danger"
                loading={remove.isPending}
                onClick={() => remove.mutate(removing)}
              >
                {t('rbac.assignmentRemove')}
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-text">{removing.roleName}</span>
              <Badge tone={scopeTone(removing.scope)}>{scopeLabel(removing.scope)}</Badge>
            </div>
            <p className="text-sm text-text-muted">{t('rbac.assignmentRemoveWarning')}</p>
          </div>
        </InlinePanel>
      ) : null}

      {addOpen && nested ? (
        <InlinePanel
          title={t('rbac.assignmentAdd')}
          footer={
            <>
            {missing === null ? null : (
              <span className="mr-auto text-sm text-text-muted">{missing}</span>
            )}
            <Button variant="ghost" onClick={closeAdd}>
              {t('rbac.cancel')}
            </Button>
            <Button
              loading={create.isPending}
              disabled={missing !== null}
              onClick={() => create.mutate()}
            >
              {t('rbac.save')}
            </Button>
            </>
          }
        >
          {addBody}
        </InlinePanel>
      ) : null}

      <Dialog
        open={addOpen && !nested}
        onClose={closeAdd}
        title={t('rbac.assignmentAdd')}
        footer={
          <>
            {missing === null ? null : (
              <span className="mr-auto text-sm text-text-muted">{missing}</span>
            )}
            <Button variant="ghost" onClick={closeAdd}>
              {t('rbac.cancel')}
            </Button>
            <Button
              loading={create.isPending}
              disabled={missing !== null}
              onClick={() => create.mutate()}
            >
              {t('rbac.save')}
            </Button>
          </>
        }
      >
        {addBody}
      </Dialog>

      <Dialog
        open={editing !== null && !nested}
        onClose={() => setEditing(null)}
        title={t('rbac.assignmentEditTitle')}
        footer={editFooter}
      >
        {editBody}
      </Dialog>

      <ConfirmDialog
        open={removing !== null && !nested}
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          if (removing !== null) remove.mutate(removing);
        }}
        title={t('rbac.assignmentRemoveTitle')}
        description={
          removing === null ? null : (
            <span className="flex flex-col items-center gap-2">
              <span className="flex flex-wrap items-center justify-center gap-2">
                <span className="text-sm font-medium text-text">{removing.roleName}</span>
                <Badge tone={scopeTone(removing.scope)}>{scopeLabel(removing.scope)}</Badge>
              </span>
              {/* KMO xatosidan o'rganilgan xatti-harakat foydalanuvchiga
                  so'zma-so'z aytiladi — u tugmani bosishdan oldin o'qiydi. */}
              <span>{t('rbac.assignmentRemoveWarning')}</span>
            </span>
          )
        }
        confirmLabel={t('rbac.assignmentRemove')}
        cancelLabel={t('rbac.cancel')}
        loading={remove.isPending}
      />
    </Card>
  );
};
